import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Icon, ShowingSettingsModal } from 'components';
import type { ListingConfigData } from 'components/ShowingSettingsModal';
import { useBreakpoint } from 'hooks';
import { getUnconfiguredUserListings } from 'api/ListingController';
import { featureEnabled } from 'utils';
import ActionItemWeb from './ActionItemWeb';
import ActionItemMobile from './ActionItemMobile';

const ActionsRequiredSection = () => {
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState<ListingConfigData | null>(null);
  const [needRefresh, setNeedRefresh] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
  const { md } = useBreakpoint();

  const fetchUnconfiguredListings = async () => {
    if (!featureEnabled('showingSettingsFeature')) return;

    const d = await getUnconfiguredUserListings();
    if (!d.listings.length) return;

    setData(d);
  };

  useEffect(() => {
    fetchUnconfiguredListings();
  }, []);

  useEffect(() => {
    if (needRefresh) {
      fetchUnconfiguredListings();
      setNeedRefresh(false);
    }
  }, [needRefresh]);

  const openShowingSettingsModal = (index?: number) => {
    setSelectedIndex(index);
    setOpenModal(true);
  };

  const reOrderedData = useMemo(() => {
    if (!selectedIndex || !data || selectedIndex === 0) return data;

    const updatedListings = [...data.listings];
    const updatedShowingConfigs = [...data.showingConfigs];
    const selectedListing = updatedListings.splice(selectedIndex, 1);
    const selectedConfig = updatedShowingConfigs.splice(selectedIndex, 1);
    updatedListings.unshift(selectedListing[0]);
    updatedShowingConfigs.unshift(selectedConfig[0]);

    return {
      listings: updatedListings,
      showingConfigs: updatedShowingConfigs,
    };
  }, [selectedIndex, data]);

  if (!data?.listings?.length) return null;

  return (
    <>
      <StyledSection $md={md}>
        <StyledTitle data-cy="action-required-title">
          <Icon type="circle" color="red7" />
          Action Required
        </StyledTitle>
        <StyledText data-cy="action-required-description">
          These listings are missing access instructions. Please provide access
          details to facilitate showings.
        </StyledText>
        {md ? (
          <ActionItemWeb
            listings={data?.listings ?? []}
            onClick={openShowingSettingsModal}
          />
        ) : (
          <ActionItemMobile
            listings={data?.listings ?? []}
            onClick={openShowingSettingsModal}
          />
        )}
      </StyledSection>
      <ShowingSettingsModal
        open={openModal}
        setOpen={setOpenModal}
        data={reOrderedData}
        setData={setData}
        setNeedRefresh={setNeedRefresh}
      />
    </>
  );
};

const StyledSection = styled.div<{ $md: boolean }>`
  background: #fee0e0;
  margin: ${({ $md }) => ($md ? '0px -32px 32px' : '0px')};
  padding: ${({ $md }) => ($md ? '24px 32px 32px' : '24px')};
`;

const StyledTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  span {
    margin-right: 8px;
  }
`;

const StyledText = styled.p`
  margin-left: 26px;
`;

export default ActionsRequiredSection;
